import { CallShellState } from '@callcenter-app/service/call-state.machine';
import { isWorkplaceUrl } from '@callcenter-app/service/workplace.config';
import { buildCallBarView } from '@callcenter-app/components/call-bar/call-bar.view';
import { TranslateFn } from '@callcenter-app/components/order-live/order-due';

/**
 * The shift card on top of the new menu (the canvas, board «Меню — состояния», 3): who is on the shift, the number, the line and the call. The call is the held one of the shell — the very texts of the call band
 * (`buildCallBarView`: «Звонит · 0:08», «Разговор 02:14», «Разговор завершён 3 мин назад»), so the card and the band never say two things. Pure — the translator and the clocks are given.
 *
 *   offline   no network, or the bus of the calls dropped after it was open — grey
 *   ringing   the held call rings — orange, «Открыть рабочее место»
 *   talking   the held call talks — green, «Вернуться к разговору»
 *   finished  the held call ended (the band still shows it) — blue
 *   noNumber  no number picked — blue, «Выбрать номер» when there are numbers to pick
 *   idle      on the line, waiting — blue
 *
 * The buttons that lead to the workplace are not drawn on the workplace itself.
 */
export type ShiftKind = 'idle' | 'ringing' | 'talking' | 'finished' | 'noNumber' | 'offline';
export type ShiftAction = 'workplace' | 'pickNumber';

export interface ShiftCardInput {
  /** The shell of stage 4; absent when it is off — then only the number and the network count. */
  state?: CallShellState;
  /** The operator's number; `''` — none. */
  slot: string;
  /** «Оператор» — the position of the user (`auth.user.position`). */
  position: string;
  /** The network is up and (when the shell is on) the bus of the calls has not dropped. */
  online: boolean;
  url: string;
  nowMs: number;
  wallMs: number;
  lang: string;
  tr: TranslateFn;
}

export interface ShiftCardView {
  kind: ShiftKind;
  /** «Оператор · номер 103». */
  role: string;
  title: string;
  sub?: string;
  action?: { label: string; act: ShiftAction };
  /** The clock of a ringing or talking call for the menu item «Рабочее место» and the folded rail: «02:14». */
  live?: string;
}

export function buildShiftCardView(input: ShiftCardInput): ShiftCardView {
  const { tr, slot } = input;
  const role = slot ? tr('shell.shift.role', { position: input.position, slot }) : tr('shell.shift.roleNoNumber', { position: input.position });
  if (!input.online) {
    return { kind: 'offline', role, title: tr('shell.shift.offline'), sub: tr('shell.shift.offlineSub') };
  }
  const onWorkplace = isWorkplaceUrl(input.url);
  if (input.state) {
    // the held call only: a new call waiting behind it speaks on the band, the card keeps the conversation in hand
    const band = buildCallBarView({
      state: { ...input.state, pending: undefined },
      url: '/',
      nowMs: input.nowMs,
      wallMs: input.wallMs,
      orderOpen: false,
      hasOrderTarget: false,
      onOrderPage: false,
      lang: input.lang,
      tr,
    });
    if (band?.kind === 'ringing') {
      return {
        kind: 'ringing', role, title: band.lead, sub: band.who || undefined, live: clockOf(band.lead),
        action: onWorkplace ? undefined : { label: tr('callBar.openWorkplace'), act: 'workplace' },
      };
    }
    if (band?.kind === 'talking') {
      return {
        kind: 'talking', role, title: band.lead, sub: band.who || undefined, live: clockOf(band.lead),
        action: onWorkplace ? undefined : { label: tr('callBar.backToCall'), act: 'workplace' },
      };
    }
    if (band?.kind === 'finished') {
      return { kind: 'finished', role, title: band.lead, sub: band.who || undefined };
    }
  }
  if (!slot) {
    const canPick = !input.state || input.state.slotsAvailable;
    return {
      kind: 'noNumber', role, title: tr('callBar.slotNone'), sub: tr('callBar.slotWho'),
      action: canPick ? { label: tr('callBar.slotPick'), act: 'pickNumber' } : undefined,
    };
  }
  return { kind: 'idle', role, title: tr('shell.shift.idle') };
}

/** The last «m:ss» / «mm:ss» / «h:mm:ss» of the lead of the band («Разговор 02:14» → «02:14»). */
function clockOf(lead: string): string | undefined {
  const m = /(\d+:\d{2}(?::\d{2})?)\s*$/.exec(lead);
  return m ? m[1] : undefined;
}
